import {
  Flavours,
  MasterProductData,
  ProductChoices,
  Products,
  ToppingOptions,
  Toppings,
} from '../../components/Catering/ProductDetailsPage/ProductInterface/ProductInterface';
import { multipleAndCheck, ternaryCheck } from '../../Utils/WindowUtils';
import _isEmpty from 'lodash/isEmpty';
import { ShowMore } from '../storeInterface';

export const getSelectedProduct = (products: Products[]) => {
  const selectedProduct = products?.find(
    (product: Products) => product.selected && product.inStock,
  );

  if (!selectedProduct) return {};

  return {
    ...selectedProduct,
  };
};

export const getSelectedProductChoices = (products: Products[]) => {
  const selectedProduct = products?.find(
    (product: Products) => product.selected && product.inStock,
  );

  if (!selectedProduct || !selectedProduct.productChoices) return [];

  return selectedProduct.productChoices;
};

export const getSelectedToppings = (products: Products[]) => {
  const selectedProduct = products?.find(
    (product: Products) => product.selected && product.inStock,
  );

  if (!selectedProduct || !selectedProduct.toppings) return [];

  return selectedProduct.toppings;
};

export const getSelectedProductMIAMChoices = (products: Products[]) => {
  const selectedProduct = products?.find(
    (product: Products) => product.selected && product.inStock,
  );

  if (!selectedProduct || !selectedProduct.miamChoice) return [];

  return selectedProduct.miamChoice;
};

const flavourSelection = (choice: ProductChoices, variantSizeSelected: number) => {
  const selectedFlavours = choice.flavours.filter(flavour => flavour.preselectedQuantity > 0);

  const totalPreselectedQuantity = selectedFlavours.reduce(
    (sum, flavour) => sum + flavour.preselectedQuantity,
    0,
  );

  const scaleFactor = variantSizeSelected / totalPreselectedQuantity;

  const baseQuantities = selectedFlavours.map(flavour =>
    Math.floor(flavour.preselectedQuantity * scaleFactor),
  );

  let remainingQuantity = variantSizeSelected - baseQuantities.reduce((sum, q) => sum + q, 0);

  const finalQuantities = baseQuantities.map(quantity => {
    if (remainingQuantity > 0) {
      remainingQuantity--;
      return quantity + 1;
    }
    return quantity;
  });

  return {
    ...choice,
    flavours: choice.flavours.map(flavour => {
      const selectedIndex = selectedFlavours.findIndex(f => f.id === flavour.id);
      const isSelected = selectedIndex !== -1;
      const quantity = ternaryCheck(isSelected, finalQuantities[selectedIndex], 0);

      return {
        ...flavour,
        selected: isSelected,
        quantity,
      };
    }),
  };
};

const toppingSelection = (toppingOptions: ToppingOptions[]) => {
  return toppingOptions?.map(option => {
    return {
      ...option,
      isSelected: option.isDefault,
      isRemove: option.min !== option.max,
    };
  });
};

export const getUpdatedProducts = (data: MasterProductData, sizeVariant: number) => {
  return data?.products?.map((product: Products) => {
    return {
      ...product,
      selected: product.isDefaultBuild && product.inStock,
      productChoices: product.productChoices?.map((choice: ProductChoices) => {
        return flavourSelection(choice, sizeVariant);
      }),
      toppings: product.toppings?.map((topping: Toppings) => {
        return {
          ...topping,
          options: toppingSelection(topping.options),
        };
      }),
      miamChoice: product.miamChoice?.map((choice: ProductChoices) => {
        return flavourSelection(choice, sizeVariant);
      }),
    };
  });
};

export const getProductHeaderInfo = (data: MasterProductData) => {
  return {
    productMainTitle: data?.prouductTitle,
    feedQuantityPhrase: data?.feedQuantityPhrase,
    description: data?.description,
    shortDescription: data?.shortDescription,
  };
};

export const getVariantDetails = (data: MasterProductData) => {
  return {
    variants: data.variants,
    reset: data.resetText,
    apply: data.applyText,
    customQuantityInfo: data.customQuantityInfo,
    customErrorInfo: data.errorText,
    customQuantityTitle: data.customQuantityTitle,
    variantTitle: data.variantTitle,
    maxOrderQuantity: data.maxOrderQuantity,
    minOrderQuantity: data.minOrderQuantity,
  };
};

export const getVariantSize = (data: MasterProductData) => {
  const { minOrderQuantity, products } = data;

  const flavorQuantity = products
    .filter(product => product.isDefaultBuild && product.inStock)
    .flatMap(product => product.productChoices.map(choice => choice.requiredQuantity))
    .reduce((total, qty) => total + qty, 0);

  return minOrderQuantity * flavorQuantity;
};

export const ingredientSelectedQuantity = (products: Products[]) => {
  return products
    .filter(product => product.selected && product.inStock)
    .flatMap(product => product.productChoices)
    .flatMap(choiceFlavour => choiceFlavour.flavours)
    .filter(option => option.selected)
    .reduce((sum, option) => sum + option.quantity, 0);
}

export const isIncrementDisabledSelector = (products: Products[], size: number) => {
  const totalQuantity = ingredientSelectedQuantity(products);

  return totalQuantity >= size;
};

export const ingredientMAIMSelectedQuantity = (products: Products[], index: number) => {
  return products
    .filter(product => product.selected && product.inStock)
    .flatMap(product => product.miamChoice)
    .filter((_, idx) => idx === index)
    .flatMap(choiceFlavour => choiceFlavour.flavours)
    .filter(option => option.selected)
    .reduce((sum, option) => sum + option.quantity, 0);
}

export const isMIAMIncrementDisabledSelector = (
  products: Products[],
  size: number,
  index: number,
) => {
  const totalQuantity = ingredientMAIMSelectedQuantity(products, index);

  return totalQuantity >= size;
};


export const getSelectedProductsAndFlavours = (products: Products[]) => {
  const selectedProduct = products?.find(product => product.selected);
 
  if (!selectedProduct) {
    return null;
  }
 
  const getModifiers = () => {
    return selectedProduct?.toppings?.[0]?.options
      ?.filter(option => option.isSelected)
      ?.map(option => ({
        itemId: option.id,
        description: option.name,
        price: 0,
        quantity: 1,
        modifierGrpId: option.modifierGroupId,
        modifierGrpName: option.modifierGroupId,
      }));
  };
 
  const transformChoices = (choices: ProductChoices[]) => {
    return choices?.map((choice: ProductChoices) => ({
      choiceOrder: choice.choiceOrder,
      description: choice.description,
      choiceItems: choice.flavours
        ?.filter((flavour: Flavours) => flavour.selected)
        ?.map((flavour: Flavours) => ({
          itemId: flavour.id,
          description: flavour.productName,
          productType: 'product',
          quantity: flavour.quantity,
          modifiers: getModifiers(),
        })),
    }));
  };
 
  const merge = [
    ...(selectedProduct?.productChoices || []),
    ...(selectedProduct?.miamChoice || [])
  ];
  
  return {
    itemId: selectedProduct?.productId,
    description: 'Classic Lunch Boxes',
    productType: selectedProduct?.productType,
    quantity: 1,
    price: selectedProduct?.price,
    choices: transformChoices(merge),
  };
};


export const footerSelectedList = (products: Products[]) => {
  const selectedProduct = products?.find(product => product.selected);

  if (!selectedProduct) {
    return null;
  }
  const formatFlavours = selectedProduct.productChoices.map(choice => {
    return choice.flavours
      .filter(flavour => flavour.selected)
      .map(flavour => {
        return `${flavour.productName} (${flavour.quantity})`;
      })
      .join(', ');
  });

  const formatToppings = selectedProduct?.toppings?.map(topping => {
    return topping.options
      .filter(option => option.isSelected)
      .map(option => {
        return option.name;
      })
      .join(', ');
  })
    .filter(topping => topping.trim() !== '');

  const toppingValues = ternaryCheck(
    formatToppings?.length > 0,
    ` w/ ${formatToppings?.toString()}`,
    '',
  );

  return `${formatFlavours?.toString()} ${toppingValues}`;
};

export const footerMIAMSelectedList = (products: Products[], index: number) => {
  const selectedProduct = products?.find(product => product?.selected);

  if (!selectedProduct) {
    return null;
  }
  const formatFlavours = selectedProduct?.miamChoice[index]?.flavours
    .filter(flavour => flavour?.selected)
    .map(flavour => {
      return `${flavour?.productName} (${flavour?.quantity})`;
    })
    .join(', ');

  return formatFlavours?.toString();
};

export const getShowMoreCounts = (products: Products[]) => {
  const showMoreCount: ShowMore = {};
  products?.forEach(product => {
    (['productChoices', 'toppings', 'miamChoice'] as (keyof Products)[]).forEach(key => {
      const groups = product[key];
      if (Array.isArray(groups)) {
        groups.forEach((group, index) => {
          if (group && typeof group === 'object') {
            const groupKey = key === 'miamChoice' ? `${key}_${index}` : key;
            showMoreCount[groupKey] = 5;
          }
        });
      }
    });
  });
  return showMoreCount;
};

export const checkAndReplace = (description: string | undefined, selectedSize: number, flavourTitle = '') => {
  if (multipleAndCheck(!_isEmpty(description), description?.includes('{0}'))) {
    return description?.replace('{0}', `${selectedSize} ${flavourTitle}`);
  } else {
    return description;
  }
};

export const formatToppingsString = (prefix: string, data: Toppings[]) => {
  if (!prefix || !data?.length) {
    return '';
  }

  const selectedItems = data?.[0].options
    .filter(option => option.isSelected)
    .map(option => option.name);

  if (selectedItems.length === 0) {
    return '';
  }

  let formattedString;
  if (selectedItems.length === 1) {
    formattedString = prefix.replace('{0}', selectedItems[0]).replace(' and {1}', '');
  } else if (selectedItems.length === 2) {
    formattedString = prefix.replace('{0}', selectedItems[0]).replace('{1}', selectedItems[1]);
  } else {
    const allButLast = selectedItems.slice(0, -1).join(', ');
    const lastItem = selectedItems[selectedItems.length - 1];
    formattedString = prefix.replace('{0}', allButLast).replace('{1}', lastItem);
  }

  return formattedString;
};

export const getToppingsArray = (data: MasterProductData | null, toppingID: string, isSelected: boolean) => {

  return data?.products.map((product: Products) => {
    return {
      ...product,
      toppings: product.toppings.map(topping => {
        return {
          ...topping,
          options: topping.options.map(option => {
            return {
              ...option,
              isSelected: ternaryCheck(option.id === toppingID, isSelected, option.isSelected),
              isRemove: ternaryCheck(option.id === toppingID, isSelected, option.isRemove),
            };
          }),
        };
      }),
    };
  }) || [];
};
export const decrementOperation = (productList: ProductChoices, decrementOptionID: string) => {
  return productList.flavours.map((option: Flavours) => {
    if (option.id === decrementOptionID) {
      const newQuantity = Math.max(option.quantity - 1, 0);
      return {
        ...option,
        quantity: newQuantity,
        selected: ternaryCheck(newQuantity > 0, option.selected, false),
      };
    }
    return option;
  })
};